import React, { useMemo } from "react";
import ThinScroll from "../../shared/ThinScroll";
import { fmtCurrency } from "../../shared/format.js";

/**
 * ForecastTable — forward_exposure evidence component.
 *
 * 15-row table of upcoming dates: Date · High · Rain % · Comfort · Tracked arrivals · Revenue.
 * Rough days are highlighted with their supplied reason (never derived).
 * Hero figure above: revenue_on_rough_days.
 *
 * Spec references: §7 (forward_exposure), §6 rule 4 (never "Arrivals"), §2d (formatting).
 */

/** Format number with locale separators. */
const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Short date — "Aug 10" */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Day of week — "Sun" */
const dayOfWeek = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

/** Comfort index colour — lower is worse. */
const comfortColor = (idx) => {
  if (idx <= 20) return "text-red-500 dark:text-red-400";
  if (idx <= 40) return "text-orange-500 dark:text-orange-400";
  if (idx <= 60) return "text-yellow-500 dark:text-yellow-400";
  return "text-green-500 dark:text-green-400";
};

const ForecastTable = ({ panel }) => {
  const f = panel.facts || {};
  const days = f.days || [];
  const roughDays = f.rough_days || [];

  // Build a lookup: date → reason for rough days
  const roughLookup = useMemo(() => {
    const map = {};
    roughDays.forEach((r) => {
      map[r.date] = r.reason;
    });
    return map;
  }, [roughDays]);

  if (days.length === 0) return null;

  return (
    <div className="space-y-4">
      {/* ─── Hero: revenue on rough days ─────────────────────────────────────── */}
      {roughDays.length === 0 ? (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <svg className="w-5 h-5 shrink-0 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-green-700 dark:text-green-300">
              No rough days forecast
            </span>
            <span className="text-xs text-green-600 dark:text-green-400">
              Clear weather ahead across the next 15 days
            </span>
          </div>
        </div>
      ) : (
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">
            {fmtCurrency(f.revenue_on_rough_days, { dropCents: true })}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Revenue on rough days
          </span>
        </div>
      )}

      {/* ─── Table ───────────────────────────────────────────────────────────── */}
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold w-24 min-w-[6rem]">Date</th>
              <th className="px-3 py-2.5 text-right font-semibold">High</th>
              <th className="px-3 py-2.5 text-right font-semibold">Rain %</th>
              <th className="px-3 py-2.5 text-right font-semibold">Comfort</th>
              <th className="px-3 py-2.5 text-right font-semibold">Tracked arrivals</th>
              <th className="px-3 py-2.5 text-right font-semibold">Revenue</th>
              <th className="px-3 py-2.5 text-left font-semibold">Reason</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => {
              const isRough = !!roughLookup[day.date];
              const reason = roughLookup[day.date] || null;

              return (
                <tr
                  key={day.date}
                  className={`border-b border-gray-100 dark:border-gray-800 transition ${
                    isRough
                      ? "bg-orange-50/60 dark:bg-orange-900/10"
                      : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }`}
                >
                  {/* Date */}
                  <td data-label="Date" className="px-3 py-2.5 whitespace-nowrap font-mono tabular-nums text-left">
                    <span className="text-gray-400 dark:text-gray-500 mr-1">
                      {dayOfWeek(day.date)}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {shortDate(day.date)}
                    </span>
                  </td>

                  {/* High temp */}
                  <td data-label="High" className="px-3 py-2.5 text-right whitespace-nowrap">
                    <span className="font-semibold text-orange-500 dark:text-orange-400">{Math.round(day.temp_high_f)}°</span>
                  </td>

                  {/* Rain % */}
                  <td data-label="Rain %" className="px-3 py-2.5 text-right whitespace-nowrap">
                    <span className={day.precip_probability_pct >= 50
                      ? "font-semibold text-blue-500 dark:text-blue-400"
                      : "text-gray-700 dark:text-gray-300"
                    }>
                      {day.precip_probability_pct}%
                    </span>
                  </td>

                  {/* Comfort index */}
                  <td data-label="Comfort" className="px-3 py-2.5 text-right whitespace-nowrap">
                    <span className={`font-semibold ${comfortColor(day.comfort_index)}`}>
                      {day.comfort_index}
                    </span>
                  </td>

                  {/* Tracked arrivals — spec §6 rule 4: never "Arrivals" */}
                  <td data-label="Tracked arrivals" className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {fmtNum(day.arrivals)}
                  </td>

                  {/* Revenue */}
                  <td data-label="Revenue" className="px-3 py-2.5 text-right whitespace-nowrap">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fmtCurrency(day.revenue_on_date)}
                    </span>
                  </td>

                  {/* Reason — only on rough days, use supplied reason verbatim */}
                  <td data-label="Reason" className="px-3 py-2.5">
                    {reason ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                        {reason}
                      </span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ThinScroll>
    </div>
  );
};

export default ForecastTable;